import { CategoriesAction } from 'module/Redux/Categories';
import { Dispatchable, Stabilizer, Stabilizers, State, dispatchable } from 'module/Redux';
import { MainSelector } from 'module/Redux/Main';
import { MenuAction } from 'module/Redux/Menu';
import { nn, se } from 'module/Common';
import { PageAction } from 'module/Redux/Page';
import { PostAction } from 'module/Redux/Post';
import { PostListAction } from 'module/Redux/PostList';
import { push } from '@etienne-dldc/redux-router-location';
import { Route, RouteType } from 'module/Model/Routes';
import { RouterAction, RouterSelector } from 'module/Redux/Router';
import { Wish } from 'module/Model/Wish';
import { WishCollection } from 'module/Model/WishCollection';

const ensureMenuFetched: Stabilizer = s => {
  if (Wish.isVoid(se(s, 'menu', s.menu))) {
    return MenuAction.fetchMenu();
  }
  return null;
};

const ensureCategoriesFetched: Stabilizer = s => {
  if (Wish.isVoid(se(s, 'categories', s.categories))) {
    return CategoriesAction.fetchCategories();
  }
  return null;
};

const ensurePostListFetchedForRoute = (state: State, routeType: RouteType | null): Dispatchable => {
  if (!routeType) {
    return null;
  }
  const needPostList = routeType === RouteType.Category || routeType === RouteType.Home || routeType === RouteType.Post;
  if (needPostList && Wish.isVoid(se(state, 'postList', state.postList))) {
    return PostListAction.fetchPostList();
  }
  return null;
};

const ensurePostListFetched: Stabilizer = s => {
  const routeType = RouterSelector.selectMatchingRouteType(s);
  return ensurePostListFetchedForRoute(s, routeType);
};

const ensurePostListRequestedFetched: Stabilizer = s => {
  const route = RouterSelector.selectRequestedMatchingRoute(s);
  const routeType = route === null ? null : route.type;
  return ensurePostListFetchedForRoute(s, routeType);
};

const ensurePageFetchedForRoute = (state: State, route: Route | null): Dispatchable => {
  if (!route || !route.type || route.type !== RouteType.Page) {
    return null;
  }
  const pageId = nn(route.id);
  if (WishCollection.selectIsVoidByKey(se(state, 'page', state.page), pageId)) {
    return PageAction.fetchPage(pageId);
  }
  return null;
};

const ensureCurrentPageFetched: Stabilizer = s => {
  const route = RouterSelector.selectMatchingRoute(s);
  return ensurePageFetchedForRoute(s, route);
};

const ensureRequestedPageFetched: Stabilizer = s => {
  const route = RouterSelector.selectRequestedMatchingRoute(s);
  return ensurePageFetchedForRoute(s, route);
};

const ensurePostFetchedForRoute = (state: State, route: Route | null): Dispatchable => {
  if (!route || !route.type || route.type !== RouteType.Post) {
    return null;
  }
  const postId = nn(route.id);
  if (WishCollection.selectIsVoidByKey(se(state, 'post', state.post), postId)) {
    return PostAction.fetchPost(postId);
  }
  return null;
};

const ensureCurrentPostFetched: Stabilizer = s => {
  const route = RouterSelector.selectMatchingRoute(s);
  return ensurePostFetchedForRoute(s, route);
};

const ensureRequestedPostFetched: Stabilizer = s => {
  const route = RouterSelector.selectRequestedMatchingRoute(s);
  return ensurePostFetchedForRoute(s, route);
};

const ensureOverscrollBehavior: Stabilizer = s => {
  const touchDetected = MainSelector.isTouchDetected(s);
  if (touchDetected) {
    if ((document.body.style as any).overscrollBehaviorY !== 'auto') {
      (document.body.style as any).overscrollBehaviorY = 'auto';
    }
  } else {
    if ((document.body.style as any).overscrollBehaviorY !== 'none') {
      (document.body.style as any).overscrollBehaviorY = 'none';
    }
  }
  return null;
};

const navigateWhenRequestedReady: Stabilizer = state => {
  const route = RouterSelector.selectRequestedMatchingRoute(state);
  const isRequestedReady = RouterSelector.selectRequested(state).ready;
  const shouldNavigate: boolean = (() => {
    if (!isRequestedReady) {
      return false;
    }
    if (route && route.type && route.type === RouteType.Post) {
      const postId = nn(route.id);
      const postWish = WishCollection.selectByKey(se(state, 'post', state.post), { key: postId });
      if (Wish.isDone(postWish)) {
        return true;
      }
    }
    if (route && route.type && route.type === RouteType.Page) {
      const pageId = nn(route.id);
      const pageWish = WishCollection.selectByKey(se(state, 'page', state.page), { key: pageId });
      if (Wish.isDone(pageWish)) {
        return true;
      }
    }
    if (route && route.type && (route.type === RouteType.Home || route.type === RouteType.Category)) {
      if (Wish.isDone(se(state, 'postList', state.postList))) {
        return true;
      }
    }
    return false;
  })();
  if (shouldNavigate) {
    const requestedLocation = nn(RouterSelector.selectRequested(state).location);
    return dispatchable([RouterAction.resetRequestedRoute(), push(requestedLocation)]);
  }
  return null;
};

export const stabilizers: Stabilizers = [
  ensureMenuFetched,
  ensureCategoriesFetched,
  ensurePostListFetched,
  ensurePostListRequestedFetched,
  navigateWhenRequestedReady,
  ensureCurrentPageFetched,
  ensureRequestedPageFetched,
  ensureCurrentPostFetched,
  ensureRequestedPostFetched,
  ensureOverscrollBehavior,
];



// WEBPACK FOOTER //
// ./src/module/View/stabilizer/index.ts